// Browser security-violation reporting for runtime fail-closed decisions.
// Reporting is bounded and best-effort so it cannot weaken enforcement.
import {
  readRuntimeSecurityPolicy,
  type LuisRuntimeSecurityViolationEvent,
  type LuisRuntimeSecurityViolationType,
} from '@lentystyle/guard/runtime'

type RuntimeSecurityEventInput = Omit<LuisRuntimeSecurityViolationEvent, 'timestamp'>

const DEFAULT_EVENT_CAP = 50
const DEFAULT_BATCH_SIZE = 5
let emittedEvents = 0
let queuedEvents: LuisRuntimeSecurityViolationEvent[] = []

// Emit one bounded security event to callback and optional beacon endpoint.
export function emitRuntimeSecurityViolation(input: RuntimeSecurityEventInput): void {
  const policy = readRuntimeSecurityPolicy()
  const maxEvents = normalizePositiveInteger(policy.maxSecurityEventsPerSession, DEFAULT_EVENT_CAP)
  // Drop events after the session cap to keep telemetry from becoming a DoS path.
  if (emittedEvents >= maxEvents) {
    return
  }

  emittedEvents += 1
  const event: LuisRuntimeSecurityViolationEvent = { ...input, timestamp: Date.now() }
  deliverViolationCallback(policy.onSecurityViolation, event)
  queueViolationReport(event, policy.reportingEndpoint, policy.securityReportBatchSize)
}

// Build one concise runtime security event payload.
export function buildRuntimeSecurityViolation(
  type: LuisRuntimeSecurityViolationType,
  message: string,
  extra: Partial<Omit<RuntimeSecurityEventInput, 'type' | 'message'>> = {},
): RuntimeSecurityEventInput {
  // Return a stable event shape for all runtime emitters.
  return { type, message, ...extra }
}

// Deliver callback synchronously but fail-soft for application handlers.
function deliverViolationCallback(
  callback: ((event: LuisRuntimeSecurityViolationEvent) => void) | undefined,
  event: LuisRuntimeSecurityViolationEvent,
): void {
  // Skip callback delivery when policy does not provide a handler.
  if (!callback) {
    return
  }

  try {
    callback(event)
  } catch {
    // Ignore telemetry callback failures to preserve fail-closed enforcement.
  }
}

// Queue endpoint delivery and flush only at configured batch size.
function queueViolationReport(
  event: LuisRuntimeSecurityViolationEvent,
  endpoint: string | undefined,
  rawBatchSize: number | undefined,
): void {
  const normalizedEndpoint = endpoint?.trim()
  // Skip network reporting when no endpoint is configured.
  if (!normalizedEndpoint) {
    return
  }

  queuedEvents.push(event)
  const batchSize = normalizePositiveInteger(rawBatchSize, DEFAULT_BATCH_SIZE)
  // Keep events queued until the configured batch size is reached.
  if (queuedEvents.length < batchSize) {
    return
  }

  flushViolationReports(normalizedEndpoint)
}

// Flush queued events as NDJSON with sendBeacon when the browser supports it.
function flushViolationReports(endpoint: string): void {
  const batch = queuedEvents
  queuedEvents = []
  const body = batch.map((event) => JSON.stringify(event)).join('\n')
  // Use sendBeacon only when available because reporting must not block runtime.
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/x-ndjson' }))
  }
}

// Normalize positive integer policy fields with one safe fallback.
function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  // Return bounded integer values only when policy provides a useful number.
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.floor(value)
  }

  return fallback
}
